<template>
  <div class="floating-navbar">
    <VCard
      class="floating-navbar__card"
      rounded="xl"
      elevation="0"
      :class="{ 'floating-navbar__card--scrolled': isScrolled }"
    >
      <div class="floating-navbar__bar">
        <!-- Brand -->
        <button
          type="button"
          class="floating-navbar__brand"
          aria-label="TVET MIS home"
          @click="goHome"
        >
          <VAvatar size="42" rounded="lg" class="floating-navbar__logo">
            <VImg :src="logo" alt="TVET MIS" cover />
          </VAvatar>
          <div class="d-none d-sm-block min-width-0 text-left">
            <div class="floating-navbar__title">TVET MIS</div>
            <div class="floating-navbar__subtitle">Somalia · MoLSA</div>
          </div>
        </button>

        <!-- Desktop nav pill -->
        <nav
          class="floating-navbar__nav d-none d-md-flex"
          aria-label="Main navigation"
        >
          <button
            v-for="item in navItems"
            :key="item.route"
            type="button"
            class="floating-navbar__nav-btn"
            :class="{
              'floating-navbar__nav-btn--active': isActiveRoute(item.route),
            }"
            @click="handleNavigation(item)"
          >
            <span
              class="floating-navbar__nav-icon"
              :class="{
                'floating-navbar__nav-icon--active': isActiveRoute(item.route),
              }"
            >
              <VIcon :icon="item.icon" size="16" />
            </span>
            <span class="floating-navbar__nav-label">{{ item.title }}</span>
            <span
              v-if="isActiveRoute(item.route)"
              class="floating-navbar__nav-dot"
              aria-hidden="true"
            />
          </button>
        </nav>

        <!-- Desktop actions -->
        <div class="floating-navbar__actions d-none d-md-flex">
          <template v-if="!authStore.isAuthenticated">
            <VBtn
              variant="text"
              rounded="lg"
              class="floating-navbar__ghost"
              to="/login"
            >
              Sign In
            </VBtn>
            <VBtn
              color="primary"
              variant="flat"
              rounded="lg"
              class="floating-navbar__cta"
              to="/online-applications"
            >
              <VIcon start size="18">mdi-file-document-edit-outline</VIcon>
              Apply Online
            </VBtn>
          </template>
          <template v-else>
            <VBtn
              variant="tonal"
              color="primary"
              rounded="lg"
              class="floating-navbar__ghost"
              to="/admin/dashboard"
            >
              <VIcon start size="18">mdi-view-dashboard-outline</VIcon>
              Dashboard
            </VBtn>
            <VMenu location="bottom end" transition="scale-transition">
              <template #activator="{ props: menuProps }">
                <button
                  type="button"
                  class="floating-navbar__user-trigger"
                  v-bind="menuProps"
                  :aria-label="authStore.userName"
                >
                  <span class="floating-navbar__user-avatar">
                    {{ userInitials }}
                  </span>
                </button>
              </template>
              <VCard
                rounded="xl"
                class="floating-navbar__menu"
                min-width="240"
              >
                <div class="floating-navbar__menu-head">
                  <div
                    class="floating-navbar__user-avatar floating-navbar__user-avatar--lg"
                  >
                    {{ userInitials }}
                  </div>
                  <div class="min-width-0">
                    <div class="floating-navbar__menu-name text-truncate">
                      {{ authStore.userName }}
                    </div>
                    <div class="floating-navbar__menu-sub">Signed in</div>
                  </div>
                </div>
                <VList density="comfortable" class="pa-2">
                  <VListItem
                    to="/admin/dashboard"
                    prepend-icon="mdi-view-dashboard-outline"
                    title="Dashboard"
                    rounded="lg"
                  />
                  <VListItem
                    prepend-icon="mdi-logout"
                    title="Sign Out"
                    rounded="lg"
                    class="text-error"
                    @click="handleLogout"
                  />
                </VList>
              </VCard>
            </VMenu>
          </template>
        </div>

        <!-- Mobile toggle -->
        <VBtn
          class="d-md-none floating-navbar__menu-toggle"
          icon
          variant="tonal"
          color="primary"
          :aria-label="mobileMenuOpen ? 'Close menu' : 'Open menu'"
          @click="toggleMobileMenu"
        >
          <VIcon size="22">
            {{ mobileMenuOpen ? "mdi-close" : "mdi-menu" }}
          </VIcon>
        </VBtn>
      </div>

      <!-- Mobile panel -->
      <VExpandTransition>
        <div v-show="mobileMenuOpen" class="floating-navbar__mobile">
          <div class="floating-navbar__mobile-glance">
            <div class="floating-navbar__mobile-glance-label">Explore</div>
            <div class="floating-navbar__mobile-glance-value">
              {{ navItems.length }} sections
            </div>
            <div class="floating-navbar__mobile-glance-sub">
              Institutions, trainers, applications & more
            </div>
          </div>

          <div class="floating-navbar__mobile-section">Navigation</div>

          <div class="floating-navbar__mobile-nav">
            <button
              v-for="item in navItems"
              :key="item.route"
              type="button"
              class="floating-navbar__mobile-item"
              :class="{
                'floating-navbar__mobile-item--active': isActiveRoute(
                  item.route
                ),
              }"
              @click="handleMobileNavigation(item)"
            >
              <span
                class="floating-navbar__nav-icon"
                :class="{
                  'floating-navbar__nav-icon--active': isActiveRoute(
                    item.route
                  ),
                }"
              >
                <VIcon :icon="item.icon" size="18" />
              </span>
              <span class="floating-navbar__mobile-item-label">{{
                item.title
              }}</span>
              <VIcon
                v-if="isActiveRoute(item.route)"
                icon="mdi-check-circle"
                size="18"
                class="floating-navbar__mobile-check"
              />
              <VIcon
                v-else
                icon="mdi-chevron-right"
                size="18"
                class="text-medium-emphasis"
              />
            </button>
          </div>

          <div class="floating-navbar__mobile-footer">
            <template v-if="!authStore.isAuthenticated">
              <VBtn
                block
                variant="outlined"
                rounded="lg"
                class="mb-3 floating-navbar__ghost"
                to="/login"
                @click="mobileMenuOpen = false"
              >
                Sign In
              </VBtn>
              <VBtn
                block
                color="primary"
                variant="flat"
                rounded="lg"
                class="floating-navbar__cta"
                to="/online-applications"
                @click="mobileMenuOpen = false"
              >
                <VIcon start size="18">mdi-file-document-edit-outline</VIcon>
                Apply Online
              </VBtn>
            </template>
            <template v-else>
              <div class="floating-navbar__drawer-user mb-4">
                <span class="floating-navbar__user-avatar">{{
                  userInitials
                }}</span>
                <div class="min-width-0">
                  <div class="floating-navbar__user-name text-truncate">
                    {{ authStore.userName }}
                  </div>
                  <div class="floating-navbar__user-role text-truncate">
                    {{ authStore.userRole || "Signed in" }}
                  </div>
                </div>
              </div>
              <VBtn
                block
                color="primary"
                variant="tonal"
                rounded="lg"
                class="mb-3 floating-navbar__ghost"
                to="/admin/dashboard"
                @click="mobileMenuOpen = false"
              >
                Go To Dashboard
              </VBtn>
              <VBtn
                block
                variant="text"
                rounded="lg"
                color="error"
                @click="handleLogout"
              >
                Sign Out
              </VBtn>
            </template>
          </div>
        </div>
      </VExpandTransition>
    </VCard>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import logo from "@/assets/images/logo.svg";

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const isScrolled = ref(false);
const mobileMenuOpen = ref(false);

const navItems = [
  {
    title: "Institutions",
    icon: "mdi-domain",
    route: "/tvet-institutions",
    action: "scroll",
  },
  {
    title: "Trainers",
    icon: "mdi-human-male-board-poll",
    route: "/tvet-trainers",
    action: "scroll",
  },
  {
    title: "Applications",
    icon: "mdi-laptop",
    route: "/online-applications",
    action: "scroll",
  },
  {
    title: "Announcements",
    icon: "mdi-bullhorn-outline",
    route: "/announcements",
    action: "scroll",
  },
  {
    title: "Resources",
    icon: "mdi-book-open-page-variant-outline",
    route: "/resource-hub",
    action: "scroll",
  },
];

const userInitials = computed(() => {
  const name = authStore.userName || "U";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
});

const handleScroll = () => {
  isScrolled.value = window.scrollY > 50;
};

const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

const isActiveRoute = (path) => {
  if (path.startsWith("#")) {
    return route.hash === path;
  }
  return route.path === path || route.path.startsWith(`${path}/`);
};

const goHome = () => {
  mobileMenuOpen.value = false;
  router.push("/");
};

const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const handleNavigation = (item) => {
  if (item.action === "scroll" && item.route.startsWith("#")) {
    scrollToSection(item.route.substring(1));
  } else {
    router.push(item.route);
  }
};

const handleMobileNavigation = (item) => {
  handleNavigation(item);
  mobileMenuOpen.value = false;
};

const handleLogout = async () => {
  mobileMenuOpen.value = false;
  await authStore.logout();
  await router.push("/login");
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
  handleScroll();
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});
</script>

<style scoped>
.floating-navbar {
  position: fixed;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  width: calc(100% - 32px);
  max-width: 1240px;
  pointer-events: none;
  font-family: "Public Sans", system-ui, -apple-system, "Segoe UI", Roboto,
    sans-serif;
  -webkit-font-smoothing: antialiased;
}

.floating-navbar__card {
  pointer-events: all;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(18px);
  border: 1px solid rgba(15, 23, 42, 0.08) !important;
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.1) !important;
  transition:
    background 0.25s ease,
    box-shadow 0.25s ease,
    border-color 0.25s ease,
    transform 0.25s ease;
}

.floating-navbar__card--scrolled {
  background: rgba(255, 255, 255, 0.97) !important;
  border-color: rgba(var(--v-theme-primary), 0.16) !important;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.14) !important;
}

.floating-navbar__bar {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 68px;
  padding: 10px 14px;
}

.floating-navbar__brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px;
  border-radius: 14px;
  transition:
    background-color 0.16s ease,
    transform 0.16s ease;
}

.floating-navbar__brand:hover {
  background: rgba(var(--v-theme-primary), 0.06);
  transform: translateY(-1px);
}

.floating-navbar__logo {
  background: #fff;
  border: 1px solid rgba(var(--v-theme-primary), 0.16);
  padding: 4px;
  box-shadow: 0 6px 14px rgba(var(--v-theme-primary), 0.12);
  flex-shrink: 0;
}

.floating-navbar__title {
  font-size: 0.95rem;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #1e1b4b;
  line-height: 1.15;
}

.floating-navbar__subtitle {
  font-size: 0.56rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
  margin-top: 2px;
}

.floating-navbar__nav {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: auto;
  margin-right: auto;
  padding: 4px;
  border-radius: 999px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  max-width: 100%;
  overflow-x: auto;
}

.floating-navbar__nav-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: none;
  background: transparent;
  color: #64748b;
  font: inherit;
  padding: 7px 10px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background-color 0.16s ease,
    color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.16s ease;
}

.floating-navbar__nav-btn:hover {
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
  transform: translateY(-1px);
}

.floating-navbar__nav-btn--active {
  color: #fff !important;
  background: rgb(var(--v-theme-primary)) !important;
  box-shadow: 0 8px 18px rgba(var(--v-theme-primary), 0.28);
}

.floating-navbar__nav-icon {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(148, 163, 184, 0.18);
  color: inherit;
}

.floating-navbar__nav-btn--active .floating-navbar__nav-icon {
  background: rgba(255, 255, 255, 0.22);
}

.floating-navbar__mobile-item--active .floating-navbar__nav-icon {
  background: rgba(var(--v-theme-primary), 0.14);
  color: rgb(var(--v-theme-primary));
}

.floating-navbar__nav-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.2;
}

.floating-navbar__nav-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.25);
}

.floating-navbar__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.floating-navbar__ghost {
  font-family: "Public Sans", system-ui, -apple-system, "Segoe UI", Roboto,
    sans-serif !important;
  text-transform: uppercase !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
}

.floating-navbar__cta {
  font-family: "Public Sans", system-ui, -apple-system, "Segoe UI", Roboto,
    sans-serif !important;
  text-transform: uppercase !important;
  font-size: 0.7rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.08em !important;
  box-shadow: 0 8px 18px rgba(var(--v-theme-primary), 0.28);
}

.floating-navbar__user-trigger {
  display: inline-flex;
  align-items: center;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  border-radius: 12px;
  transition: transform 0.16s ease;
}

.floating-navbar__user-trigger:hover {
  transform: translateY(-1px);
}

.floating-navbar__user-avatar {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: #fff;
  background: rgb(var(--v-theme-primary));
  box-shadow: 0 6px 14px rgba(var(--v-theme-primary), 0.28);
}

.floating-navbar__user-avatar--lg {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  font-size: 0.85rem;
}

.floating-navbar__user-name {
  font-size: 0.82rem;
  font-weight: 700;
  color: #1e1b4b;
  line-height: 1.2;
}

.floating-navbar__user-role {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-primary));
}

.floating-navbar__menu {
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.14);
  overflow: hidden;
}

.floating-navbar__menu-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: rgb(var(--v-theme-primary));
  color: #fff;
}

.floating-navbar__menu-name {
  font-size: 0.95rem;
  font-weight: 800;
}

.floating-navbar__menu-sub {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.85;
  margin-top: 2px;
}

.floating-navbar__menu :deep(.v-list-item-title) {
  font-family: "Public Sans", system-ui, -apple-system, "Segoe UI", Roboto,
    sans-serif;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.06em !important;
  text-transform: uppercase !important;
}

.floating-navbar__menu-toggle {
  margin-left: auto;
  border-radius: 12px !important;
}

.floating-navbar__mobile {
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  background: rgba(var(--v-theme-primary), 0.03);
  padding: 14px;
}

.floating-navbar__mobile-glance {
  padding: 14px 16px;
  border-radius: 16px;
  background: rgb(var(--v-theme-primary));
  color: #fff;
  box-shadow: 0 10px 24px rgba(var(--v-theme-primary), 0.28);
}

.floating-navbar__mobile-glance-label {
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.8;
}

.floating-navbar__mobile-glance-value {
  font-size: 1.3rem;
  font-weight: 800;
  margin-top: 2px;
  line-height: 1.2;
}

.floating-navbar__mobile-glance-sub {
  font-size: 0.75rem;
  opacity: 0.85;
  margin-top: 4px;
  line-height: 1.35;
}

.floating-navbar__mobile-section {
  padding: 14px 4px 8px;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #94a3b8;
}

.floating-navbar__mobile-nav {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.floating-navbar__mobile-item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 12px;
  border: 1px solid transparent;
  border-radius: 14px;
  background: #fff;
  color: #334155;
  cursor: pointer;
  transition:
    background-color 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    transform 0.16s ease;
}

.floating-navbar__mobile-item:hover {
  border-color: rgba(var(--v-theme-primary), 0.16);
  background: rgba(var(--v-theme-primary), 0.05);
  transform: translateY(-1px);
}

.floating-navbar__mobile-item--active {
  background: rgba(var(--v-theme-primary), 0.16);
  border-color: rgba(var(--v-theme-primary), 0.18);
  box-shadow: inset 3px 0 0 rgb(var(--v-theme-primary));
  color: rgb(var(--v-theme-primary));
}

.floating-navbar__mobile-item-label {
  flex: 1;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.floating-navbar__mobile-check {
  color: rgb(var(--v-theme-primary));
}

.floating-navbar__mobile-footer {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
}

.floating-navbar__drawer-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid rgba(var(--v-theme-primary), 0.14);
}

.min-width-0 {
  min-width: 0;
}

@media (max-width: 960px) {
  .floating-navbar {
    width: calc(100% - 20px);
    top: 8px;
  }
}

@media (max-width: 600px) {
  .floating-navbar {
    width: calc(100% - 12px);
    top: 6px;
  }

  .floating-navbar__bar {
    min-height: 60px;
    padding: 8px 10px;
  }
}
</style>
